import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import type { Doctor, DoctorStatus } from "../../schemas/doctor.schema";
import DoctorProfileModal from "./DoctorProfileModal";

// TODO(API): onStatusChange will call PATCH /doctors/:id { status: DoctorStatus }
// TODO(API): onDeactivate will call DELETE /doctors/:id (soft delete — sets is_active=false)
// TODO(API): onScheduleClick should navigate to /roster?doctorId=:id (not yet implemented)
// TODO(API): onProfileClick should navigate to /doctors/:id detail page (not yet implemented)

interface DoctorCardProps {
  doctor: Doctor;
  /** null/undefined = status not yet fetched; "pending" = invite not yet accepted; "accepted" = account set up */
  inviteStatus?: "pending" | "accepted" | null;
  onResendInvite?: () => void;
  onStatusChange?: (doctorId: string, status: DoctorStatus) => void;
  onDeactivate?: (doctorId: string) => void;
  onScheduleClick?: (doctorId: string) => void;
  onProfileClick?: (doctorId: string) => void;
}

// Deterministic avatar color from userId — avoids color shift on re-render
const AVATAR_COLORS = [
  { bg: "bg-blue-500",    text: "text-white" },
  { bg: "bg-emerald-500", text: "text-white" },
  { bg: "bg-orange-500",  text: "text-white" },
  { bg: "bg-indigo-500",  text: "text-white" },
  { bg: "bg-rose-500",    text: "text-white" },
  { bg: "bg-amber-500",   text: "text-white" },
  { bg: "bg-teal-500",    text: "text-white" },
  { bg: "bg-purple-500",  text: "text-white" },
];

function avatarColor(userId: string) {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = userId.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitials(firstName: string, lastName: string) {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

const STATUS_CONFIG: Record<
  DoctorStatus,
  { label: string; dot: string; pill: string }
> = {
  present: {
    label: "Present",
    dot: "bg-emerald-500",
    pill: "bg-emerald-50 text-emerald-700",
  },
  break: {
    label: "Break",
    dot: "bg-amber-500",
    pill: "bg-amber-50 text-amber-700",
  },
  leave: {
    label: "On Leave",
    dot: "bg-red-400",
    pill: "bg-red-50 text-red-700",
  },
  off: {
    label: "Off Today",
    dot: "bg-slate-400",
    pill: "bg-slate-100 text-slate-600",
  },
};

const KebabMenu: React.FC<{
  doctorId: string;
  currentStatus: DoctorStatus;
  inviteStatus?: "pending" | "accepted" | null;
  onStatusChange?: (id: string, s: DoctorStatus) => void;
  onDeactivate?: (id: string) => void;
  onResendInvite?: () => void;
}> = ({ doctorId, currentStatus, inviteStatus, onStatusChange, onDeactivate, onResendInvite }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const statusOptions: DoctorStatus[] = ["present", "break", "leave", "off"];

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="p-1.5 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
        aria-label="Doctor options"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="5" r="1.5"/>
          <circle cx="12" cy="12" r="1.5"/>
          <circle cx="12" cy="19" r="1.5"/>
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 top-8 z-20 bg-white border border-slate-200 rounded-xl shadow-lg py-1 w-44 overflow-hidden">
          <p className="px-3 py-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">
            Set Status
          </p>
          {statusOptions
            .filter((s) => s !== currentStatus)
            .map((s) => (
              <button
                key={s}
                onClick={() => { onStatusChange?.(doctorId, s); setOpen(false); }}
                className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2 transition-colors"
              >
                <span className={`w-2 h-2 rounded-full ${STATUS_CONFIG[s].dot}`} />
                {STATUS_CONFIG[s].label}
              </button>
            ))}
          <div className="mx-3 border-t border-slate-100 my-1" />
          {inviteStatus === "pending" && onResendInvite && (
            <button
              onClick={() => { onResendInvite(); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm text-amber-700 hover:bg-amber-50 transition-colors flex items-center gap-2"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10"/>
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
              </svg>
              Resend invite
            </button>
          )}
          <button
            onClick={() => { onDeactivate?.(doctorId); setOpen(false); }}
            className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
          >
            Deactivate Doctor
          </button>
        </div>
      )}
    </div>
  );
};

const DoctorCard: React.FC<DoctorCardProps> = ({
  doctor,
  inviteStatus,
  onResendInvite,
  onStatusChange,
  onDeactivate,
  onScheduleClick,
  onProfileClick,
}) => {
  const navigate = useNavigate();
  const [showProfile, setShowProfile] = useState(false);
  const color = avatarColor(doctor.userId);
  const initials = getInitials(doctor.firstName, doctor.lastName);
  const status = STATUS_CONFIG[doctor.status];

  return (
    <>
    {showProfile && (
      <DoctorProfileModal doctor={doctor} onClose={() => setShowProfile(false)} />
    )}
    <div className={`bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex flex-col gap-4 hover:shadow-md transition-shadow${!doctor.isActive ? " opacity-50" : ""}`}>
      {/* Header: avatar + name + status + kebab */}
      <div className="flex items-start gap-3">
        <div
          className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold ${color.bg} ${color.text}`}
        >
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-slate-900 truncate leading-snug">
            Dr. {doctor.firstName} {doctor.lastName}
          </h3>
          <p className="text-xs text-slate-500 truncate mt-0.5">{doctor.specialty}</p>
          {/* Status pill */}
          {doctor.isActive === false ? (
            <span className="inline-flex items-center gap-1.5 mt-1.5 text-xs font-medium px-2 py-0.5 rounded-full bg-red-50 text-red-600">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
              Inactive
            </span>
          ) : (
            <span
              className={`inline-flex items-center gap-1.5 mt-1.5 text-xs font-medium px-2 py-0.5 rounded-full ${status.pill}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
              {status.label}
            </span>
          )}
          {inviteStatus === "pending" && (
            <span className="inline-flex items-center gap-1 mt-1 text-xs font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
              Invite pending
            </span>
          )}
        </div>
        <KebabMenu
          doctorId={doctor.userId}
          currentStatus={doctor.status}
          inviteStatus={inviteStatus}
          onStatusChange={onStatusChange}
          onDeactivate={onDeactivate}
          onResendInvite={onResendInvite}
        />
      </div>

      {/* Department tag */}
      <div>
        <span className="inline-block text-xs font-medium px-2.5 py-1 rounded-lg bg-blue-50 text-blue-700">
          {doctor.department}
        </span>
      </div>

      {/* Today's Patients */}
      <div className="bg-blue-50/60 rounded-xl px-3 py-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-xs font-medium text-slate-600">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
            </svg>
            Today's Patients
          </div>
          {/* TODO(API): todayPatients.total comes from visits count joined on doctor + today's date */}
          <span className="text-base font-bold text-slate-900">
            {doctor.todayPatients.total}
          </span>
        </div>
        <p className="text-xs text-slate-400 mt-1">
          {doctor.todayPatients.completed} completed &bull;{" "}
          {doctor.todayPatients.upcoming} upcoming
        </p>
      </div>

      {/* Info rows */}
      <div className="space-y-1.5 text-xs text-slate-600">
        {/* Qualification */}
        <div className="flex items-center gap-2">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 text-slate-400">
            <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
            <path d="M6 12v5c3 3 9 3 12 0v-5"/>
          </svg>
          <span className="truncate">{doctor.qualification}</span>
        </div>
        {/* Experience + schedule */}
        <div className="flex items-center gap-2">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 text-slate-400">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
          </svg>
          <span className="truncate">
            {doctor.experienceYears} years exp &bull; {doctor.scheduleDays},{" "}
            {doctor.scheduleTime}
          </span>
        </div>
        {/* Room */}
        {doctor.room && (
          <div className="flex items-center gap-2">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 text-slate-400">
              <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
            <span className="truncate">{doctor.room}</span>
          </div>
        )}
      </div>

      {/* Stats row: Rating | Total Patients | Fee */}
      <div className="flex items-center justify-between pt-1 border-t border-slate-100">
        <div className="flex flex-col items-center gap-0.5">
          <span className="text-sm font-semibold text-slate-900">{doctor.rating.toFixed(1)}</span>
          <span className="text-[10px] text-slate-400">Rating</span>
        </div>
        <div className="w-px h-8 bg-slate-100" />
        <div className="flex flex-col items-center gap-0.5">
          {/* TODO(API): totalPatients should come from DoctorProfile or be computed from visits count */}
          <span className="text-sm font-semibold text-slate-900">{doctor.totalPatients.toLocaleString()}</span>
          <span className="text-[10px] text-slate-400">Total Patients</span>
        </div>
        <div className="w-px h-8 bg-slate-100" />
        <div className="flex flex-col items-center gap-0.5">
          <span className="text-sm font-semibold text-slate-900">₹{doctor.consultationFee.toLocaleString()}</span>
          <span className="text-[10px] text-slate-400">Fee</span>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-2 pt-1">
        <button
          onClick={() => {
            onScheduleClick?.(doctor.userId);
            navigate(`/roster?doctorId=${doctor.displayId || doctor.userId}`);
          }}
          className="flex-1 flex items-center justify-center gap-1.5 min-h-[44px] text-xs font-medium rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-colors"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect width="18" height="18" x="3" y="4" rx="2" ry="2"/>
            <line x1="16" x2="16" y1="2" y2="6"/>
            <line x1="8" x2="8" y1="2" y2="6"/>
            <line x1="3" x2="21" y1="10" y2="10"/>
          </svg>
          Schedule
        </button>
        <button
          onClick={() => {
            onProfileClick?.(doctor.userId);
            setShowProfile(true);
          }}
          className="flex-1 flex items-center justify-center gap-1.5 min-h-[44px] text-xs font-medium rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-colors"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>
          Profile
        </button>
      </div>
    </div>
    </>
  );
};

export default DoctorCard;
